/**
 * Patient Portal Authentication Context
 * 
 * Separate auth context for Behavioral Health patient portal users.
 * Does NOT share credentials with staff/admin login system.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { safeStorage } from '../utils/safeStorage';
import { PRODUCTION_API_BASE_URL } from '../config/apiOrigin';
const PatientAuthContext = createContext(null);
let redirectingToPatientLogin = false;

const clearStoredPatientPortalAuth = () => {
  safeStorage.removeItem('patient_portal_token');
  safeStorage.removeItem('patient_portal_refresh_token');
  safeStorage.removeItem('patient_portal_user');
  safeStorage.removeItem('patient_portal_organization');
};

const redirectToPatientLogin = () => {
  clearStoredPatientPortalAuth();
  if (redirectingToPatientLogin || window.location.pathname === '/patient-portal/login') return;

  redirectingToPatientLogin = true;
  window.location.assign('/patient-portal/login');
};

// API base URL - use VITE_API_BASE_URL and add /v1 suffix, with production fallback
const API_BASE = import.meta.env.VITE_API_BASE_URL 
  ? `${import.meta.env.VITE_API_BASE_URL}/v1`
  : (import.meta.env.PROD 
      ? `${PRODUCTION_API_BASE_URL}/v1`
      : 'http://localhost:3002/api/v1');

// Create axios instance for patient portal
const patientApi = axios.create({
  baseURL: `${API_BASE}/patient-portal`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth interceptor
patientApi.interceptors.request.use((config) => {
  const token = safeStorage.getItem('patient_portal_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for token expiration
patientApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    const isAuthRequest = requestUrl.startsWith('/auth/');
    
    // If token expired, try to refresh
    if (error.response?.status === 401 &&
        error.response?.data?.code === 'TOKEN_EXPIRED' &&
        !isAuthRequest &&
        !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = safeStorage.getItem('patient_portal_refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE}/patient-portal/auth/refresh`, {
            refreshToken
          });
          
          const { accessToken } = response.data;
          safeStorage.setItem('patient_portal_token', accessToken);
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return patientApi(originalRequest);
        }
      } catch (refreshError) {
        // A refresh failure means this browser no longer has a valid portal session.
        redirectToPatientLogin();
      }
    }

    // Never render an authenticated shell with blank data after a revoked or
    // invalid session. Authentication endpoints remain on the login screen so
    // their own validation messages can be shown.
    if (error.response?.status === 401 && !isAuthRequest) {
      redirectToPatientLogin();
    }
    
    return Promise.reject(error);
  }
);

export function PatientAuthProvider({ children }) {
  const [patient, setPatient] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(null);

  // Load stored auth on mount
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const token = safeStorage.getItem('patient_portal_token');
        const storedUser = safeStorage.getItem('patient_portal_user');
        const storedOrg = safeStorage.getItem('patient_portal_organization');
        
        if (token && storedUser) {
          const storedPatient = JSON.parse(storedUser);
          // Validate restored sessions before protected pages mount. This
          // avoids a dashboard/profile/documents request race against a
          // revoked token, which previously left the profile blank.
          const response = await patientApi.get('/profile');
          const validatedPatient = {
            ...storedPatient,
            ...response.data.profile,
            email: response.data.portalEmail || storedPatient.email,
            mfaEnabled: response.data.mfaEnabled
          };
          safeStorage.setItem('patient_portal_user', JSON.stringify(validatedPatient));
          setPatient(validatedPatient);
          if (storedOrg) {
            setOrganization(JSON.parse(storedOrg));
          }
        }
      } catch (e) {
        // Corrupted, expired, or revoked saved auth must not be treated as a
        // signed-in portal session. The interceptor redirects rejected tokens.
        clearStoredPatientPortalAuth();
      } finally {
        setLoading(false);
      }
    };
    
    loadStoredAuth();
  }, []);

  /**
   * Login with email and password
   * Returns { success, requiresMfa, sessionId } or { success: false, error }
   */
  const login = useCallback(async (email, password, organizationSlug) => {
    try {
      const response = await patientApi.post('/auth/login', {
        email,
        password,
        organizationSlug
      });
      
      const data = response.data;
      
      // Check if MFA is required
      if (data.requiresMfa) {
        setMfaPending({
          mfaChallengeToken: data.mfaChallengeToken,
          mfaMethods: data.mfaMethods
        });
        return { success: true, requiresMfa: true };
      }
      
      // Full login success
      safeStorage.setItem('patient_portal_token', data.accessToken);
      safeStorage.setItem('patient_portal_refresh_token', data.refreshToken);
      safeStorage.setItem('patient_portal_user', JSON.stringify(data.patient));
      safeStorage.setItem('patient_portal_organization', JSON.stringify(data.organization));
      
      setPatient(data.patient);
      setOrganization(data.organization);
      setMfaPending(null);
      
      return { success: true, requiresMfa: false };
      
    } catch (error) {
      console.error('Patient login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed',
        code: error.response?.data?.code
      };
    }
  }, []);

  /**
   * Verify MFA code after login
   */
  const verifyMfa = useCallback(async (code) => {
    if (!mfaPending) {
      return { success: false, error: 'No pending MFA verification' };
    }
    
    try {
      const response = await patientApi.post('/auth/verify-mfa', { mfaChallengeToken: mfaPending.mfaChallengeToken, code });
      
      const data = response.data;
      
      safeStorage.setItem('patient_portal_token', data.accessToken);
      safeStorage.setItem('patient_portal_refresh_token', data.refreshToken);
      safeStorage.setItem('patient_portal_user', JSON.stringify(data.patient));
      safeStorage.setItem('patient_portal_organization', JSON.stringify(data.organization));
      
      setPatient(data.patient);
      setOrganization(data.organization);
      setMfaPending(null);
      
      return { success: true };
      
    } catch (error) {
      console.error('MFA verification error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Invalid code'
      };
    }
  }, [mfaPending]);

  /**
   * Logout and clear session
   */
  const logout = useCallback(async () => {
    try {
      await patientApi.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      safeStorage.removeItem('patient_portal_token');
      safeStorage.removeItem('patient_portal_refresh_token');
      safeStorage.removeItem('patient_portal_user');
      safeStorage.removeItem('patient_portal_organization');
      setPatient(null);
      setOrganization(null);
      setMfaPending(null);
    }
  }, []);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = !!patient && !!safeStorage.getItem('patient_portal_token');

  const refreshUser = useCallback(async () => {
    if (!isAuthenticated) return;
    const response = await patientApi.get('/profile');
    const nextPatient = {
      ...patient,
      ...response.data.profile,
      email: response.data.portalEmail || patient?.email,
      mfaEnabled: response.data.mfaEnabled,
    };
    safeStorage.setItem('patient_portal_user', JSON.stringify(nextPatient));
    setPatient(nextPatient);
  }, [isAuthenticated, patient]);

  const value = {
    patient,
    user: patient,
    organization,
    loading,
    isLoading: loading,
    isAuthenticated,
    mfaPending,
    login,
    verifyMfa,
    logout,
    refreshUser,
    patientApi // Expose API client for components
  };

  return (
    <PatientAuthContext.Provider value={value}>
      {children}
    </PatientAuthContext.Provider>
  );
}

export function usePatientAuth() {
  const context = useContext(PatientAuthContext);
  if (!context) {
    throw new Error('usePatientAuth must be used within a PatientAuthProvider');
  }
  return context;
}

export default PatientAuthContext;
